import React, { useCallback, useState } from 'react';
import { token } from '@atlaskit/tokens';

import {
  useHAMSCreatePaymentMethod,
  useHAMSRenewablePaymentConfirm,
  CreditCardForm,
  CreditCardErrorMessage,
} from '@atlassian/commerce-ui/credit-card-hams';
import { styled } from 'styled-components';

import { Font } from 'components/visuals';
import { useInactivityTimer } from 'hooks/useInactivityTimer';
import t from 'modules/i18n/intl';

import {
  storePaymentDetails as storeCheckoutPaymentDetails,
  storeMACCreditCardDetails,
  handleFailures,
  isSuccessful,
  useHAMSGateway,
} from 'modules/stripe';

const StyledTimeElapsingMessage = styled.div`
  color: ${token('color.text.subtle', '#44546F')};
  font-family: ${Font.displayFamily};
  font-size: 13px;
`;

const useSubmitCallback = (
  storePaymentDetails,
  HAMSPaymentMethodHook: typeof useHAMSCreatePaymentMethod | typeof useHAMSRenewablePaymentConfirm,
) => {
  const createPaymentMethod = HAMSPaymentMethodHook();
  const gateway = useHAMSGateway();

  const submitCallback = useCallback(async () => {
    const result = await createPaymentMethod!();

    handleFailures(result);

    if (!isSuccessful(result)) {
      return result;
    }

    const { paymentMethod } = result.payload;

    storePaymentDetails(gateway!, paymentMethod);

    return result;
  }, [createPaymentMethod, gateway]);

  if (createPaymentMethod === null) {
    return null;
  }

  return submitCallback;
};

export const useSubmitPaymentMethod = () =>
  useSubmitCallback(storeCheckoutPaymentDetails, useHAMSCreatePaymentMethod);

export const useStripeForMAC = () =>
  useSubmitCallback(storeMACCreditCardDetails, useHAMSRenewablePaymentConfirm);

export const Form = () => {
  const [formKey, setFormKey] = useState(0);

  const clearCreditCardForm = useCallback(() => {
    setFormKey((prevKey) => prevKey + 1);
  }, []);

  const minutes = useInactivityTimer(clearCreditCardForm) || 0;

  return (
    <div>
      <CreditCardForm key={formKey} autoFocus frameEmbeddingMitigation={false} />
      <StyledTimeElapsingMessage>
        {t('credit-card-form.inactivity.message', { count: minutes })}.
      </StyledTimeElapsingMessage>
    </div>
  );
};

export { CreditCardErrorMessage as ErrorMessage };
